import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Clock, Loader2, Play, Save, Send, Users } from 'lucide-react';
import { api } from '@/api/client';

type MisWindow = 'yesterday' | 'today' | 'last7' | 'last30' | 'last45' | 'mtd';

interface Schedule {
  enabled: boolean;
  hour: number;
  minute: number;
  window: MisWindow;
  orderType: '' | 'B2C' | 'B2B';
  pacingMs: number;
  subject: string;
  body: string;
}
interface SchedulePayload {
  schedule: Schedule;
  /** Field names mirror `windowRange()` on the server — startDate/endDate, not from/to. */
  window: { startDate: string; endDate: string; label: string };
  enabledMerchants: number;
}
interface RunResult {
  merchants: number;
  sent: number;
  failed: number;
  skipped: number;
  results: Array<{ merchant: string; recipients: number; status: string; rows?: number; error?: string }>;
}

const WINDOWS: { value: MisWindow; label: string }[] = [
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'today', label: 'Today so far' },
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last30', label: 'Last 30 days' },
  { value: 'last45', label: 'Last 45 days' },
  { value: 'mtd', label: 'Month to date' },
];

/**
 * The daily MIS run — when it fires, what period each sheet covers, and who it would reach.
 *
 * WHICH COLUMNS each merchant gets is deliberately NOT here: that is a per-merchant decision and
 * lives on their own Notifications tab, next to the address the sheet is sent to. This screen only
 * decides the timing, which is platform-wide.
 *
 * The dry run is placed ahead of the save on purpose. Arming this mails every opted-in client on
 * the platform, and the honest order of operations is read-then-arm — the same pattern the wallet
 * backfill and the weight backlog follow.
 */
export function MisSchedulePanel() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Schedule | null>(null);
  const [preview, setPreview] = useState<RunResult | null>(null);
  const [previewIsReal, setPreviewIsReal] = useState(false);

  const { data, isLoading } = useQuery<SchedulePayload>({
    queryKey: ['mis-schedule'],
    queryFn: async () => (await api.get('/master-data/schedule')).data,
  });

  const s = draft ?? data?.schedule ?? null;
  const dirty = draft !== null;
  const set = (patch: Partial<Schedule>) => s && setDraft({ ...s, ...patch });

  const save = useMutation({
    mutationFn: async () => (await api.post('/master-data/schedule', s)).data,
    onSuccess: () => {
      toast.success('MIS schedule saved');
      setDraft(null);
      qc.invalidateQueries({ queryKey: ['mis-schedule'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save'),
  });

  const dryRun = useMutation({
    mutationFn: async () => (await api.get('/master-data/schedule/dry-run')).data as RunResult,
    onSuccess: (r) => { setPreview(r); setPreviewIsReal(false); toast.success(`Dry run: ${r.sent} would be sent`); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Dry run failed'),
  });

  const runNow = useMutation({
    mutationFn: async () => (await api.post('/master-data/schedule/run-now')).data as RunResult,
    onSuccess: (r) => {
      setPreview(r); setPreviewIsReal(true);
      toast.success(`Sent to ${r.sent} merchant(s)`);
      qc.invalidateQueries({ queryKey: ['mis-schedule'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Run failed'),
  });

  if (isLoading || !s) return <div className="py-10 text-center text-slate-400"><Loader2 size={22} className="animate-spin mx-auto" /></div>;

  const enabledMerchants = data?.enabledMerchants ?? 0;
  const hhmm = `${String(s.hour).padStart(2, '0')}:${String(s.minute).padStart(2, '0')}`;

  return (
    <div className="space-y-4 max-w-4xl">
      <div>
        <h2 className="text-lg font-semibold text-slate-800">MIS Schedule</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          The daily master-data sheet, emailed to each merchant's MIS recipients. Which columns a
          merchant receives is set on <b>that merchant's Notifications tab</b>.
        </p>
      </div>

      <div className={`rounded-lg border p-3 flex items-start gap-2 text-sm ${
        s.enabled ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-slate-200 bg-slate-50 text-slate-600'
      }`}>
        {s.enabled ? <CheckCircle2 size={16} className="shrink-0 mt-0.5" /> : <AlertTriangle size={16} className="shrink-0 mt-0.5 text-slate-400" />}
        <div>
          {s.enabled
            ? <>The daily run is <b>ON</b>. It fires at <b>{hhmm} IST</b> and currently reaches <b>{enabledMerchants}</b> merchant(s).</>
            : <>The daily run is <b>off</b>. Nothing is emailed on a schedule until it is switched on here.</>}
          <div className="text-xs mt-1 opacity-80">
            A merchant only receives the sheet once <b>Send the scheduled MIS</b> is ticked on their
            own Notifications tab — {enabledMerchants} merchant(s) are opted in right now.
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={s.enabled} onChange={(e) => set({ enabled: e.target.checked })} className="w-4 h-4 accent-primary" />
          <span className="text-sm font-semibold text-slate-700">Send the MIS automatically every day</span>
        </label>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Send at (IST)</label>
            <div className="relative">
              <Clock size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="time"
                value={hhmm}
                onChange={(e) => {
                  const [h, m] = e.target.value.split(':').map(Number);
                  set({ hour: h || 0, minute: m || 0 });
                }}
                className="input w-full pl-7"
              />
            </div>
            {/* The tick is every 15 minutes, so saying "08:00" and meaning "within the quarter-hour
                after 08:00" is honest about what actually happens. */}
            <p className="text-[11px] text-slate-400 mt-1">Fires within 15 minutes of this time.</p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Period covered</label>
            <select value={s.window} onChange={(e) => set({ window: e.target.value as MisWindow })} className="input w-full">
              {WINDOWS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
            </select>
            {data?.window && (
              <p className="text-[11px] text-slate-400 mt-1">
                Today that is {data.window.startDate === data.window.endDate
                  ? data.window.startDate
                  : `${data.window.startDate} → ${data.window.endDate}`}.
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Service</label>
            <select value={s.orderType} onChange={(e) => set({ orderType: e.target.value as any })} className="input w-full">
              <option value="">B2C + B2B in one sheet</option>
              <option value="B2C">B2C only</option>
              <option value="B2B">B2B only</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Gap between merchants</label>
            <select value={s.pacingMs} onChange={(e) => set({ pacingMs: Number(e.target.value) })} className="input w-full">
              <option value={0}>No gap</option>
              <option value={800}>0.8 seconds</option>
              <option value={1500}>1.5 seconds</option>
              <option value={3000}>3 seconds</option>
            </select>
            <p className="text-[11px] text-slate-400 mt-1">Protects the sending domain's reputation.</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Subject</label>
            <input value={s.subject} onChange={(e) => set({ subject: e.target.value })} className="input w-full" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Body</label>
            <textarea value={s.body} onChange={(e) => set({ body: e.target.value })} rows={4} className="input w-full font-mono text-xs" />
          </div>
        </div>
        <p className="text-[11px] text-slate-400 -mt-2">
          Placeholders: <code>{'{{merchant}}'}</code> · <code>{'{{window}}'}</code> · <code>{'{{rows}}'}</code>
        </p>

        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-100">
          <button onClick={() => save.mutate()} disabled={save.isPending || !dirty}
            className="mt-3 px-5 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2">
            {save.isPending ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
          </button>
          <button onClick={() => dryRun.mutate()} disabled={dryRun.isPending}
            className="mt-3 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2">
            {dryRun.isPending ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} Dry run (sends nothing)
          </button>
          <button
            onClick={() => {
              // Not a browser confirm() — this mails real clients, so the count is spelled out.
              if (window.prompt(`This EMAILS ${enabledMerchants} merchant(s) right now. Type SEND to confirm.`) !== 'SEND') return;
              runNow.mutate();
            }}
            disabled={runNow.isPending || enabledMerchants === 0}
            className="mt-3 px-4 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm font-medium hover:bg-amber-100 disabled:opacity-50 flex items-center gap-2">
            {runNow.isPending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send now
          </button>
          {dirty && <span className="mt-3 text-xs text-amber-600">Unsaved changes</span>}
        </div>
      </div>

      {preview && (
        <div className="rounded-lg border border-slate-200 bg-white">
          <div className="px-4 py-2.5 border-b border-slate-100 flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
              <Users size={14} className="text-slate-400" />
              {previewIsReal ? 'Run result' : 'Dry run — nothing was sent'}
            </span>
            <span className="text-xs text-slate-500">
              {preview.merchants} merchant(s) · {preview.sent} {previewIsReal ? 'sent' : 'would send'}
              {preview.skipped > 0 && <> · {preview.skipped} skipped</>}
              {preview.failed > 0 && <span className="text-rose-600"> · {preview.failed} failed</span>}
            </span>
          </div>
          {preview.results.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-slate-400">
              No merchant is opted in yet — tick <b>Send the scheduled MIS</b> on a merchant's Notifications tab.
            </div>
          ) : (
            <div className="max-h-80 overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">Merchant</th>
                    <th className="text-left px-3 py-2 font-semibold">Recipients</th>
                    <th className="text-left px-3 py-2 font-semibold">Rows</th>
                    <th className="text-left px-3 py-2 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.results.map((r, i) => (
                    <tr key={i}>
                      <td className="px-4 py-1.5 text-slate-700">{r.merchant}</td>
                      <td className="px-3 py-1.5 text-slate-500">{r.recipients}</td>
                      <td className="px-3 py-1.5 text-slate-500">{r.rows ?? '—'}</td>
                      <td className="px-3 py-1.5">
                        <span className={
                          r.status === 'FAILED' ? 'text-rose-600'
                          : r.status === 'SENT' || r.status === 'WOULD_SEND' ? 'text-emerald-700'
                          : 'text-slate-400'
                        }>
                          {r.status}{r.error ? ` — ${r.error}` : ''}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
